// RraVerifyFormPage — PUBLIC third-party verifier form (/rra-verify/:token).
// Modeled on VerificationFormPage: token-gated, no auth shell, state-aware.
// RRA v2.1 (W3; E2, E3).
//
// ONE QUESTION SET (E2): "Do you know {name} in a way that lets you answer?"
// Yes/No; a No hides the rest and says so kindly ("Thank you — that's a helpful
// answer. You can submit now."); a Yes shows ONLY the questions inside the
// recorded gap (askDisability / askNeed), each answered Yes · No · I can't say.
// "I can't say" is never a negative: it records no evidence and the request
// stays open. The disability definition sits directly above its question, and the
// requester's own words (for a non-animal change) directly above the need
// question. NOTHING free-text (no "anything else" box exists, by design: it's a
// diagnosis magnet). The server also drops any out-of-gap answer.
//
// E3: the page shows what was asked of them, a link to THEIR copy of the signed
// request (the per-verifier excerpt), the office number to check the message is
// genuine, and requires the respondent's name so we know who answered.
//
// Every sentence that is not layout comes from rraCopy.ts.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  BLANK_VERIFY_DRAFT,
  buildVerifySubmission,
  getRraVerifyForm,
  rraVerifyCopyUrl,
  submitRraVerifyForm,
  verifyDraftProblem,
  RraFormApiError,
  type RraProngAnswer,
  type RraVerifyFormDraft,
  type RraVerifyFormView,
} from '@/lib/rraFormClient';
import {
  ANSWER_WEB,
  POSITION_NO_THANKS,
  RESPONDENT_NAME_LABEL,
  VERIFY_THANKS,
  VIEW_COPY_LINK,
  changeShownAbove,
  disabilityQuestion,
  needQuestion,
  positionQuestion,
  verifyGenuineLine,
} from '@/lib/rraCopy';

// The office line — one shared constant (WEL-171: 509-525-1040 everywhere).
import { OFFICE_PHONE } from '@/lib/officeContact';

/** A question answered with a short list of radio choices, in a labelled fieldset. */
function Choice<T extends string>(props: {
  name: string;
  legend: string;
  value: T | undefined;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="label">{props.legend}</legend>
      <div className="cluster" style={{ gap: 'var(--space-5)', flexWrap: 'wrap' }}>
        {props.options.map((o) => (
          <label key={o.value} style={{ cursor: 'pointer' }}>
            <input
              type="radio"
              name={props.name}
              checked={props.value === o.value}
              onChange={() => props.onChange(o.value)}
            />{' '}
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

const POSITION_OPTIONS = [
  { value: 'yes', label: ANSWER_WEB.yes },
  { value: 'no', label: ANSWER_WEB.no },
] as const;

const PRONG_OPTIONS: ReadonlyArray<{ value: RraProngAnswer; label: string }> = [
  { value: 'yes', label: ANSWER_WEB.yes },
  { value: 'no', label: ANSWER_WEB.no },
  { value: 'cant_say', label: ANSWER_WEB.cantSay },
];

export function RraVerifyFormPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [view, setView] = useState<RraVerifyFormView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<RraVerifyFormDraft>(BLANK_VERIFY_DRAFT);

  const patch = (p: Partial<RraVerifyFormDraft>) => setDraft((d) => ({ ...d, ...p }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await getRraVerifyForm(token);
        if (!cancelled) setView(v);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = useCallback(async () => {
    if (!view) return;
    const problem = verifyDraftProblem(view, draft);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await submitRraVerifyForm(token, buildVerifySubmission(view, draft));
      setView(res.view);
    } catch (e) {
      setError(
        e instanceof RraFormApiError
          ? (e.errorMessage ?? 'Submission failed. Please try again.')
          : 'Submission failed. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }, [view, token, draft]);

  if (loading) return <Shell><p>Loading…</p></Shell>;
  if (!view || view.state === 'unavailable')
    return (
      <Shell>
        <h1 className="card-title">This link is no longer available</h1>
        <p>If you believe this is an error, contact our office at {OFFICE_PHONE}.</p>
      </Shell>
    );

  if (view.state === 'closed')
    return (
      <Shell>
        <h1 className="card-title">Nothing is needed from you</h1>
        <p>
          This request has been resolved or withdrawn — no action is needed on your part.
          If you have questions, contact our office at {OFFICE_PHONE}.
        </p>
      </Shell>
    );

  if (view.state === 'submitted')
    return (
      <Shell>
        <h1 className="card-title">Thank you</h1>
        <p>{VERIFY_THANKS}</p>
        <p style={{ color: 'var(--fg-muted)' }}>
          Need to reach us? Call our office at {OFFICE_PHONE}.
        </p>
      </Shell>
    );

  const who = view.subjectName || 'this person';
  const positionNo = draft.positionToKnow === 'no';
  const positionYes = draft.positionToKnow === 'yes';

  return (
    <Shell>
      <p style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
        A quick confirmation · Welcome Home Properties
      </p>
      <h1 className="card-title" style={{ marginBottom: 'var(--space-2)' }}>{who}</h1>
      <p style={{ color: 'var(--fg-muted)', marginTop: 0 }}>
        {who} named you as someone who can confirm information supporting a request they've
        made with us, and gave us written permission to contact you. A simple yes or no from
        your own knowledge of them is all we need — please don't include a diagnosis,
        condition names, or medical records.
      </p>
      <p style={{ marginTop: 0 }}>{view.requestLine}</p>
      {view.copyAvailable && (
        <p style={{ marginTop: 0 }}>
          <a href={rraVerifyCopyUrl(token)} target="_blank" rel="noreferrer">
            {VIEW_COPY_LINK}
          </a>
        </p>
      )}
      <p style={{ color: 'var(--fg-muted)', marginTop: 0 }}>{verifyGenuineLine(OFFICE_PHONE)}</p>
      {error && (
        <div className="alert" data-tone="danger" role="alert">
          <div className="alert-body">{error}</div>
        </div>
      )}

      <div className="stack">
        <Choice
          name="rra-verify-position"
          legend={positionQuestion(who)}
          value={draft.positionToKnow}
          options={POSITION_OPTIONS}
          onChange={(v) => patch({ positionToKnow: v })}
        />

        {positionNo && (
          <div className="alert" data-tone="info" role="status">
            <div className="alert-body">{POSITION_NO_THANKS}</div>
          </div>
        )}

        {positionYes && view.askDisability && (
          <div className="stack" style={{ gap: 'var(--space-3)' }}>
            {view.definition && (
              <div
                className="card"
                data-testid="rra-verify-definition"
                style={{ padding: 'var(--space-4)', background: 'var(--bg-subtle, var(--bg))' }}
              >
                <p style={{ marginTop: 0 }}>{view.definition.intro}</p>
                <ul style={{ margin: 0, paddingLeft: 'var(--space-5)' }}>
                  {view.definition.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
                <p style={{ marginBottom: 0 }}>{view.definition.tail}</p>
              </div>
            )}
            <Choice
              name="rra-verify-disability"
              legend={disabilityQuestion(who)}
              value={draft.disability}
              options={PRONG_OPTIONS}
              onChange={(v) => patch({ disability: v })}
            />
          </div>
        )}

        {positionYes && view.askNeed && (
          <div className="stack" style={{ gap: 'var(--space-3)' }}>
            {view.changeTexts.map((t) => (
              <p key={t} style={{ margin: 0 }}>{changeShownAbove(t)}</p>
            ))}
            <Choice
              name="rra-verify-need"
              legend={needQuestion(who, view.needObject)}
              value={draft.need}
              options={PRONG_OPTIONS}
              onChange={(v) => patch({ need: v })}
            />
          </div>
        )}

        <div className="field">
          <label className="label" htmlFor="rra-verify-name">{RESPONDENT_NAME_LABEL}</label>
          <input
            id="rra-verify-name"
            className="input"
            required
            maxLength={200}
            value={draft.name}
            onChange={(e) => patch({ name: e.target.value })}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="rra-verify-email">Your email (optional)</label>
          <input
            id="rra-verify-email"
            className="input"
            maxLength={320}
            value={draft.email}
            onChange={(e) => patch({ email: e.target.value })}
          />
        </div>
        <button
          className="btn"
          data-variant="primary"
          disabled={draft.positionToKnow === undefined || busy}
          onClick={() => void submit()}
        >
          {busy ? 'Submitting…' : 'Submit'}
        </button>
        <p style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', margin: 0 }}>
          Prefer not to use a form? Simply reply to our email, or send us a letter — both work
          just as well.
        </p>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--fg)', fontFamily: 'var(--font-sans)', padding: 'var(--space-6)' }}>
      <div className="card" style={{ maxWidth: 560, margin: '0 auto', padding: 'var(--space-6)' }}>{children}</div>
    </div>
  );
}
